import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import update from 'immutability-helper';
import _ from "lodash";
import Joi from "joi";
import http from "../services/http";
import Input from "../components/common/input";
import Select from "../components/common/select";
import UserContext from "../context/userContext";

const ManageScreen = ({ match, history }) => {
  const [user] = useContext(UserContext);

  const [screen, setScreen] = useState({
    DESCRIPTION: "",
    SCREEN_TYPE: "",
    ACCESS_LEVEL: "",
    AREA: "A",
  });
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [access, setAccess] = useState([]);
  const types = [
    { CODE: "DLY", DESCRIPTION: "Daily" },
    { CODE: "HLY", DESCRIPTION: "Hourly" },
  ];
  const areas = [
    { ID: "A", DESCRIPTION: "Data Entry" },
    { ID: "V", DESCRIPTION: "Live Data" },
  ];
  const [code, setCode] = useState("");

  const schema = {
    DESCRIPTION: Joi.string().required().max(40).label("Name"),
    SCREEN_TYPE: Joi.string().required().label("Type"),
    ACCESS_LEVEL: Joi.number().min(1).max(4).required().label("Access Level"),
    AREA: Joi.string().allow("A", "V").only(),
  };

  useEffect(() => {
    async function getAccess() {
      try {
        const { data } = await http.get(`/data-access/data-screen`);
        setAccess(data);
      }
      catch {
        //ignore error
      }
    }

    getAccess();
  }, []);

  const historyCallback = useCallback(() => {
    history.push(`/dataentry/manage`);
  }, [history]);

  useEffect(() => {
    async function getScreen() {
      try {
        const { data } = await http.get(`/data-screens/${match.params.id}`);
        setScreen(
          _.pick(data, ["DESCRIPTION", "SCREEN_TYPE", "ACCESS_LEVEL", "AREA"])
        );
      } catch (ex) {
        historyCallback();
      }
    }
    async function getParameters() {
      try {
        const { data } = await http.get(`/data-screen/heading/${match.params.id}`);

        for (let i = 0; i < data.length; i++) {
          if (data[i].LOWER_ABSOLUTE === null) data[i].LOWER_ABSOLUTE = undefined;
          if (data[i].LOWER_CHECK === null) data[i].LOWER_CHECK = undefined;
          if (data[i].UPPER_CHECK === null) data[i].UPPER_CHECK = undefined;
          if (data[i].UPPER_ABSOLUTE === null) data[i].UPPER_ABSOLUTE = undefined;
          if (data[i].PRECISION === null) data[i].PRECISION = undefined;
        }
        
        setParameters(data);
      }
      catch {
        //ignore error
      }
    }

    if (user && match.params.id !== "new") {
      getScreen();
      getParameters();
    }
  }, [user, match.params.id, historyCallback]);
  

  // Form Functions

  function validateProperty({ name, value }, schema) {
    const obj = { [name]: value };
    const propSchema = Joi.object({ [name]: schema[name] });
    const { error } = propSchema.validate(obj);
    return error ? error.details[0].message : null;
  }

  function validate() {
    const options = { abortEarly: false };
    const { error } = Joi.object(schema).validate(screen, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;
    return errors;
  }

  function handleChange({ currentTarget: input }) {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input, schema);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];
    setErrors(newErrors);

    const newScreen = { ...screen };
    newScreen[input.name] = input.value;
    setScreen(newScreen);
  }

  async function handleScreenUpdate(e) {
    e.preventDefault();

    const newErrors = validate();
    setErrors(newErrors || {});
    if (newErrors) return;

    try {
      if (match.params.id === "new") {
        const { data } = await http.post(`/data-screen`, screen);
        history.push(`/dataentry/manage/${data.ID}`);
      } else {
        const putScreen = _.pick(screen, [
          "DESCRIPTION",
          "ACCESS_LEVEL",
          "AREA",
        ]);
        await http.put(`/data-screen/${match.params.id}`, putScreen);
        toast.success("Screen Updated");
      }
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  // Parameter Functions

  async function handleParameterAdd(e) {
    e.preventDefault();
    if (code === "") return;
    try {
      const { data } = await http.post(`/data-screen-member/${match.params.id}/${code}`);
      setParameters([...parameters, data]);
      toast.success("Parameter added");
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  // Table Functions

  function handleTableChange({ currentTarget: input }, index, name) {
    // Need to solve validation to do decimals etc
    // Could just allow update to fail and feedback error as toast
    const newParameters = [...parameters];
    newParameters[index][name] = input.value;
    setParameters(newParameters);
  }

  async function handleUpdateParameter(parameter) {
    // Pick out parameter table info
    const parameterPatch = _.pick(parameter, [
      "HEADING1",
      "HEADING2",
      "HEADING3",
      "PRECISION",
      "UPPER_CHECK",
      "LOWER_CHECK",
    ]);

    try {
      await http.patch(`/parameter/data-screen/${parameter.CODE}`, parameterPatch);
      toast.success("Parameter Updated");
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  async function handleDeleteParameter(parameter) {
    const oldParameters = [...parameters];
    setParameters(parameters.filter((p) => p.ID !== parameter.ID));

    try {
      await http.delete(`/data-screen-member/${parameter.ID}`);
      toast.success(`Parameter ${parameter.CODE} Removed`);
    } catch (ex) {
      if (ex.response) {
        setParameters(oldParameters);
        console.log(ex.response.status);
        toast.error(ex.response.data);
      }
    }
  }

  async function handleSeperatorClick(parameter, index) {
    const oldParameters = [...parameters];
    const newParameters = [...parameters];
    const newSeperator = newParameters[index].SEPERATOR ? false : true;
    newParameters[index].SEPERATOR = newSeperator;
    setParameters(newParameters);

    try {
      await http.patch(`/data-screen-member/${parameter.ID}/${newSeperator}`);
      toast.success("Seperator Updated");
    } catch (ex) {
      setParameters(oldParameters);
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  async function handleDragUpdate(result) {
    setLoading(true)
    let direction
    let index = result.source.index
    const parameter = result.draggableId
    const loopNumber = Math.sqrt(Math.pow((result.destination.index - index), 2))
    if ((result.destination.index - index) > 0) {
      direction = "DOWN"
    } else {
      direction = "UP"
    }
    setParameters((prevParameters) => 
      update(prevParameters, {
        $splice: [
          [index, 1],
          [result.destination.index, 0, prevParameters[index]],
        ],
      }),
    )
    for (let i = 0; i < loopNumber; i++) {
      if (direction === "UP" && index === 0) return;
      if (direction === "DOWN" && index === parameters.length - 1) return;
      let offset = 0;
      if (direction === "UP") offset -= 1;
      if (direction === "DOWN") offset += 1;

      const offsetId = parameters[index + offset].ID;

      // Swap Sequence in DB
      try {
        await http.patch(`/data-screen-member/sequence/${parameter}/${offsetId}`);
        if (direction === "UP") index -= 1;
        if (direction === "DOWN") index += 1;
      } catch (ex) {
        if (ex.response) {
          toast.error(ex.response.data);
        }
      }
    }
    setLoading(false)
    toast.success('Screen Updated')
  }
  
  async function handleSequenceUpdate(direction, parameter, index) {
    if (direction === "UP" && index === 0) return;
    if (direction === "DOWN" && index === parameters.length - 1) return;
    let offset = 0;
    if (direction === "UP") offset -= 1;
    if (direction === "DOWN") offset += 1;

    const offsetId = parameters[index + offset].ID;

    // Swap Sequence in DB
    try {
      await http.patch(`/data-screen-member/sequence/${parameter.ID}/${offsetId}`);
      // Always update after database
      // Stops users from submitting another change without the previous one being complete
      const newParameters = [...parameters];
      const temp = newParameters[index + offset];
      const tempSeq = newParameters[index + offset].SEQUENCE;
      newParameters[index + offset] = newParameters[index];
      newParameters[index] = temp;
      // Swap Sequence Numbers here too
      newParameters[index].SEQUENCE = newParameters[index + offset].SEQUENCE;
      newParameters[index + offset].SEQUENCE = tempSeq;
      setParameters(newParameters);
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  async function handleExport() {
    const input = parameters.map((p) => {
      return _.pick(p, [
        "SEQUENCE",
        "CODE",
        "HEADING1",
        "HEADING2",
        "HEADING3",
        "PRECISION",
        "UPPER_CHECK",
        "LOWER_CHECK",
        "SEPERATOR",
      ]);
    });
    try {
      const { data } = await http.post(`/csv`, { data: input });

      const options = {
        suggestedName: 'Parameters.csv',
        types: [{
          description: "CSV file",
          accept: {'text/plain': ['.csv']}
        }]
      };

      const newHandle = await window.showSaveFilePicker(options);      

      const writableStream = await newHandle.createWritable();
      await writableStream.write(data);
      await writableStream.close();
    } 
    catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  return (
    <div className="col mt-4">
      <form className="container">
        <div className="form-row">
          <div className="col">
            <Input
              name="DESCRIPTION"
              label="Name"
              placeholder="Screen name"
              value={screen.DESCRIPTION}
              onChange={handleChange}
              error={errors.DESCRIPTION}
            />
          </div>
          <div className="col">
            <Select
              name="SCREEN_TYPE"
              label="Type"
              placeholder="Screen Type"
              value={screen.SCREEN_TYPE}
              options={types}
              onChange={handleChange}
              error={errors.SCREEN_TYPE}
              listKey="CODE"
              listName="DESCRIPTION"
              disabled={match.params.id !== "new"}
            />
          </div>
          <div className="col">
            <Select
              name="ACCESS_LEVEL"
              label="Access Level"
              placeholder="Level"
              value={screen.ACCESS_LEVEL}
              options={access}
              onChange={handleChange}
              error={errors.ACCESS_LEVEL}
              listKey="ID"
              listName="DESCRIPTION"
            />
          </div>
          <div className="col">
            <Select
              name="AREA"
              label="Screen Area"
              placeholder="Area"
              value={screen.AREA}
              options={areas}
              onChange={handleChange}
              error={errors.AREA}
              listKey="ID"
              listName="DESCRIPTION"
            />
          </div>
        </div>
        <div className="form-row">
          <button
            className="btn btn-primary ml-auto mr-5"
            onClick={handleScreenUpdate}
            disabled={validate()}
          >
            {match.params.id === "new" ? "Create" : "Update"}
          </button>
        </div>
        <hr className="bg-success my-5" />
      </form>
      {match.params.id !== "new" && (
        <form className="container">
          <div className="row">
            <div className="col-3">
              <Input
                name="CODE"
                placeholder="Parameter Code"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
              />
            </div>
            <div className="col">
              <button className="btn btn-primary" onClick={handleParameterAdd}>
                <i className="fas fa-plus"></i> Add
              </button>
            </div>
          </div>
        </form>
      )}
      {match.params.id !== "new" && (
        <div className="row">
          <button
            className="btn btn-outline-warning ml-auto mr-5"
            onClick={handleExport}
          >
            Export
          </button>
        </div>
      )}
      <div className="row">
        <div className="col">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Heading 1</th>
                  <th>Heading 2</th>
                  <th>Heading 3</th>
                  <th>Decimal</th>
                  <th>Upper Check</th>
                  <th>Lower Check</th>
                  <th>Seperator</th>
                  <th></th>
                </tr>
              </thead>
              <DragDropContext onDragEnd={handleDragUpdate}>
                <Droppable droppableId="tbody">
                  {(provided) => (
                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                      {parameters.map((parameter, index) => (
                        <Draggable key={parameter.ID.toString()} draggableId={parameter.ID.toString()} index={index} isDragDisabled={loading}>
                          {(provided) => (
                            <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} key={parameter.ID}>
                              <td className="text-nowrap">{parameter.CODE}</td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="text-input"
                                    type="text"
                                    value={parameter.HEADING1 ? parameter.HEADING1 : ""}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "HEADING1")
                                    }
                                  />
                                ) : (
                                  parameter.HEADING1
                                )}
                              </td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="text-input"
                                    type="text"
                                    value={parameter.HEADING2 ? parameter.HEADING2 : ""}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "HEADING2")
                                    }
                                  />
                                ) : (
                                  parameter.HEADING2
                                )}
                              </td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="text-input"
                                    type="text"
                                    value={parameter.HEADING3 ? parameter.HEADING3 : ""}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "HEADING3")
                                    }
                                  />
                                ) : (
                                  parameter.HEADING3
                                )}
                              </td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="number-input-sm"
                                    type="number"
                                    value={parameter.PRECISION}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "PRECISION")
                                    }
                                  />
                                ) : (
                                  parameter.PRECISION
                                )}
                              </td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="number-input-md"
                                    type="number"
                                    value={parameter.UPPER_CHECK}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "UPPER_CHECK")
                                    }
                                  />
                                ) : (
                                  parameter.UPPER_CHECK
                                )}
                              </td>
                              <td>
                                {user.PARAMETERS ? (
                                  <input
                                    name={parameter.SEQUENCE}
                                    className="number-input-md"
                                    type="number"
                                    value={parameter.LOWER_CHECK}
                                    onChange={(e) =>
                                      handleTableChange(e, index, "LOWER_CHECK")
                                    }
                                  />
                                ) : (
                                  parameter.LOWER_CHECK
                                )}
                              </td>
                              <td className="text-center">
                                <span
                                  className={
                                    parameter.SEPERATOR
                                      ? "badge badge-success badge-pointer"
                                      : "badge badge-warning badge-pointer"
                                  }
                                  onClick={() => handleSeperatorClick(parameter, index)}
                                >
                                  {parameter.SEPERATOR ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                {!loading ?
                                  index > 0 ?
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() =>
                                        handleSequenceUpdate("UP", parameter, index)
                                      }
                                    >
                                      <i className="fas fa-arrow-up"></i>
                                    </button> 
                                  :                                    
                                    <div />
                                :
                                  <div className="spinner-grow spinner-grow-sm text-success"></div>
                                }
                              </td>
                              <td>
                                {!loading ?
                                  index < parameters.length - 1 ?
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() =>
                                        handleSequenceUpdate("DOWN", parameter, index)
                                      }
                                    >
                                      <i className="fas fa-arrow-down"></i>
                                    </button>
                                  :
                                    <div />
                                :
                                  <div className="spinner-grow spinner-grow-sm text-success"></div>
                                }
                              </td>
                              <td>
                                {user.PARAMETERS && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleUpdateParameter(parameter)}
                                  >
                                    Update
                                  </button>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteParameter(parameter)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            </table>
        </div>
      </div>
    </div>
  );
};

export default ManageScreen;


